import { ConfirmationDialog, Spinner } from "@primer/react";
import { useEffect, useMemo, useState } from "react";
import { BottomTabBar, ITabItem } from "../../../components/mobile";
import { IClassListItem } from "../../../model/respone/classes/IClassListItem";
import { INhomLop } from "../../../model/respone/classes/INhomLop";
import { IMyRegistration } from "../../../model/respone/registration/IMyRegistration";
import { eReducerStatusBase } from "../../../state/reducer-model/eReducerStatusBase";
import { ISubjectGroup, formatCountdown, formatDateVi } from "../classesHelpers";
import RegisterTab from "./RegisterTab";
import RegisteredTab from "./RegisteredTab";
import RegTimeBanner from "./RegTimeBanner";
import SupportTab from "./SupportTab";
import "./mobile.css";

type MobileTabKey = "register" | "registered" | "support";

/** Khung giờ đăng ký — đã resolve ở ClassesPage: ưu tiên đợt, fallback claim JWT. */
export interface IRegTimeInfo {
    start?: string | null;
    end?: string | null;
}

/**
 * View-model truyền từ ClassesPage xuống bản mobile. Mọi state/memo/handler vẫn
 * được tính ở ClassesPage (orchestrator) — các component mobile chỉ trình bày.
 */
export interface IClassesMobileProps {
    // ----- gate / thời gian -----
    enforceTimeWindow: boolean;
    beforeOpen: boolean;
    afterClose: boolean;
    openAt?: string | null;
    closeAt?: string | null;
    openTs: number | null;
    now: number;
    registerGateReason?: string;
    queueStatus: eReducerStatusBase;
    /** Khung giờ đăng ký hiển thị ở đầu trang. */
    regTime: IRegTimeInfo;

    // ----- dữ liệu -----
    filteredSubjects: ISubjectGroup[];
    filteredCombinedClasses: IClassListItem[];
    nhomLopOptions: INhomLop[];
    registeredList: IMyRegistration[];
    myList: IMyRegistration[];

    // ----- filter -----
    hinhThuc: number;
    hinhThucReg: number;
    nhomLop: string;
    onApplyFilter: (hinhThuc: number, nhomLop: string) => void;
    onChangeHinhThucReg: (v: number) => void;

    // ----- chọn môn / mở rộng -----
    selectedSubjectKey: string | null;
    onSelectSubject: (subjectKey: string) => void;
    expandedSub: { type: "lth" | "sh"; classId: number } | null;
    onToggleExpand: (type: "lth" | "sh", classId: number) => void;

    // ----- trạng thái -----
    classesLoading: boolean;
    classesError: boolean;
    classesErrorMessage?: string;
    myLoading: boolean;
    isChotDuLieu: boolean;
    isChottingNow: boolean;
    ngayChot: Date | null;
    tongTC: number;
    tcMin: number;
    tcMax: number;
    pendingClassIds: number[];
    confirmedClassIds: number[];
    cancellingClassIds: number[];

    // ----- handlers -----
    onRegister: (classId: number) => void;
    onPickPair: (parentClassId: number, childClassId: number) => void;
    onRefreshSlots: () => void;
    onRetryLoad: () => void;
    onChotClick: () => void;

    // ----- confirm dialog -----
    confirmCancel: IMyRegistration | null;
    setConfirmCancel: (r: IMyRegistration | null) => void;
    onCancelConfirm: () => void;
    confirmChot: boolean;
    setConfirmChot: (b: boolean) => void;
    onChotConfirm: () => void;
}

/**
 * Shell bản mobile của trang đăng ký tín chỉ:
 *   - Banner gate thời gian (giữ logic như desktop).
 *   - 2 tab dưới cùng: "Đăng ký" và "Đã ĐK (N)".
 *   - ConfirmationDialog (Primer) cho hủy / chốt — tái dùng nguyên.
 */
const ClassesPageMobile = (props: IClassesMobileProps) => {
    const {
        enforceTimeWindow, beforeOpen, afterClose, openAt, closeAt, openTs, now,
        registerGateReason, queueStatus, regTime,
        filteredSubjects, filteredCombinedClasses, nhomLopOptions, registeredList, myList,
        hinhThuc, hinhThucReg, nhomLop, onApplyFilter, onChangeHinhThucReg,
        selectedSubjectKey, onSelectSubject, expandedSub, onToggleExpand,
        classesLoading, classesError, classesErrorMessage, myLoading,
        isChotDuLieu, isChottingNow, ngayChot, tongTC, tcMin, tcMax,
        pendingClassIds, confirmedClassIds, cancellingClassIds,
        onRegister, onPickPair, onRefreshSlots, onRetryLoad, onChotClick,
        confirmCancel, setConfirmCancel, onCancelConfirm,
        confirmChot, setConfirmChot, onChotConfirm,
    } = props;

    const [activeTab, setActiveTab] = useState<MobileTabKey>("register");

    /** Đã chốt dữ liệu → tab "Đăng ký" bị khóa hoàn toàn → đưa SV về tab "Đã ĐK".
     *  Chỉ chạy 1 lần lúc isChotDuLieu chuyển true; sau đó SV vẫn tự bấm xem tab khác được. */
    useEffect(() => {
        if (isChotDuLieu) setActiveTab("registered");
    }, [isChotDuLieu]);

    /** Số lớp đã đăng ký (Confirmed) — badge tab, không phụ thuộc filter hình thức. */
    const registeredCount = useMemo(
        () => myList.filter(r => r.trang_thai === 1).length,
        [myList]
    );

    /** Chặn UI đăng ký khi /queue/status chưa trả snapshot thời gian (mirror desktop). */
    const blockedForQueue =
        enforceTimeWindow &&
        queueStatus !== eReducerStatusBase.is_completed &&
        queueStatus !== eReducerStatusBase.is_error;
    const queueLoading =
        queueStatus === eReducerStatusBase.is_loading ||
        queueStatus === eReducerStatusBase.is_not_initialization;

    const tabs: ITabItem[] = [
        { key: "register", label: "Đăng ký", icon: "fas fa-list-check" },
        { key: "registered", label: "Đã ĐK", icon: "fas fa-clipboard-check", badge: registeredCount },
        { key: "support", label: "Hỗ trợ", icon: "fas fa-headset" },
    ];

    return (
        <div className="m-page">
            {/* ===== Khung giờ + banner gate — không hiện ở tab Hỗ trợ ===== */}
            {activeTab !== "support" && (
                <>
                    <RegTimeBanner start={regTime.start} end={regTime.end} />
                    {beforeOpen && openAt && (
                        <div className="m-banner m-banner-warning">
                            Chưa đến thời gian đăng ký. Cổng mở lúc{" "}
                            <strong>{formatDateVi(openAt)}</strong>.
                            {openTs != null && (
                                <> Còn lại <strong>{formatCountdown(openTs - now)}</strong>.</>
                            )}
                            <span className="m-banner-note">
                                Bạn có thể xem trước danh sách lớp. Nút "Đăng ký" sẽ bật khi đến giờ.
                            </span>
                        </div>
                    )}
                    {afterClose && closeAt && (
                        <div className="m-banner m-banner-danger">
                            Đã quá thời gian đăng ký. Cổng đóng lúc{" "}
                            <strong>{formatDateVi(closeAt)}</strong>.
                        </div>
                    )}
                    {enforceTimeWindow && registerGateReason && !beforeOpen && !afterClose && (
                        <div className="m-banner m-banner-info m-banner-row">
                            {queueLoading && <Spinner size="small" />}
                            <span>{registerGateReason}</span>
                        </div>
                    )}
                </>
            )}

            {/* ===== Nội dung tab ===== */}
            {activeTab === "register" &&
                (isChotDuLieu ? (
                    <div className="m-empty">
                        <i className="fas fa-lock" />
                        Bạn đã lưu (chốt) dữ liệu đăng ký. Không thể đăng ký thêm lớp.
                        <br />
                        Xem lại các lớp đã đăng ký ở tab "Đã ĐK".
                    </div>
                ) : (
                    <>
                        {/* Nhắc SV có lớp chưa lưu → bấm chuyển sang tab Đã ĐK để lưu */}
                        {registeredCount > 0 && (
                            <button
                                type="button"
                                className="m-nudge"
                                onClick={() => setActiveTab("registered")}
                            >
                                <i className="fas fa-circle-exclamation m-nudge-icon" />
                                <span className="m-nudge-text">
                                    Bạn có <strong>{registeredCount} lớp</strong> đã đăng ký{" "}
                                    <strong>chưa lưu</strong>. Bấm để sang lưu dữ liệu.
                                </span>
                                <i className="fas fa-arrow-right m-nudge-go" />
                            </button>
                        )}
                        <RegisterTab
                        blockedForQueue={blockedForQueue}
                        classesLoading={classesLoading}
                        classesError={classesError}
                        classesErrorMessage={classesErrorMessage}
                        onRetryLoad={onRetryLoad}
                        hinhThuc={hinhThuc}
                        nhomLop={nhomLop}
                        nhomLopOptions={nhomLopOptions}
                        onApplyFilter={onApplyFilter}
                        filteredSubjects={filteredSubjects}
                        filteredCombinedClasses={filteredCombinedClasses}
                        selectedSubjectKey={selectedSubjectKey}
                        onSelectSubject={onSelectSubject}
                        expandedSub={expandedSub}
                        onToggleExpand={onToggleExpand}
                        myList={myList}
                        pendingClassIds={pendingClassIds}
                        confirmedClassIds={confirmedClassIds}
                        gateReason={registerGateReason}
                        onRegister={onRegister}
                        onPickPair={onPickPair}
                        onRefreshSlots={onRefreshSlots}
                        />
                    </>
                ))}

            {activeTab === "registered" && (
                <RegisteredTab
                    registeredList={registeredList}
                    myLoading={myLoading}
                    tongTC={tongTC}
                    tcMin={tcMin}
                    tcMax={tcMax}
                    isChotDuLieu={isChotDuLieu}
                    isChottingNow={isChottingNow}
                    ngayChot={ngayChot}
                    onChotClick={onChotClick}
                    cancellingClassIds={cancellingClassIds}
                    gateReason={registerGateReason}
                    onCancel={setConfirmCancel}
                    hinhThucReg={hinhThucReg}
                    onChangeHinhThucReg={onChangeHinhThucReg}
                />
            )}

            {activeTab === "support" && <SupportTab />}

            {/* ===== Tab bar dưới cùng ===== */}
            <BottomTabBar
                tabs={tabs}
                activeKey={activeTab}
                onChange={k => setActiveTab(k as MobileTabKey)}
            />

            {/* ===== Confirm dialogs (Primer) ===== */}
            {confirmCancel && (
                <ConfirmationDialog
                    title="Xác nhận hủy đăng ký"
                    onClose={g => (g === "confirm" ? onCancelConfirm() : setConfirmCancel(null))}
                    confirmButtonContent="Xác nhận hủy"
                    confirmButtonType="danger"
                    cancelButtonContent="Đóng"
                >
                    Bạn có chắc muốn hủy đăng ký lớp{" "}
                    <strong>{confirmCancel.ten_lop_hp ?? `#${confirmCancel.id_lop_tc}`}</strong>?
                </ConfirmationDialog>
            )}
            {confirmChot && (
                <ConfirmationDialog
                    title="Xác nhận lưu dữ liệu đăng ký"
                    onClose={g => (g === "confirm" ? onChotConfirm() : setConfirmChot(false))}
                    confirmButtonContent="Lưu"
                    confirmButtonType="danger"
                    cancelButtonContent="Đóng"
                >
                    Lưu ý: Sinh viên tự chịu trách nhiệm với kết quả đăng ký tín chỉ. Bạn đã chắc chắn
                    muốn lưu lại kết quả đăng ký không? Sau khi bấm lưu, sinh viên không được phép thay
                    đổi kết quả đăng ký.
                </ConfirmationDialog>
            )}
        </div>
    );
};

export default ClassesPageMobile;
